import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

// ✅ GET /:clientId/budgets - Listar presupuestos con filtros
export const getBudgets = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Agregar filtros opcionales
    if (filters.status) params.append('status', filters.status);
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    if (filters.entity_table) params.append('entity_table', filters.entity_table);
    if (filters.entity_id) params.append('entity_id', filters.entity_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.limit) params.append('limit', filters.limit);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/budgets${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || response.data.data?.length || 0
    };
  } catch (error) {
    console.error("Error getting budgets:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuestos",
      status: error.response?.status
    };
  }
};

// ✅ GET /:clientId/budgets/:budgetId - Obtener presupuesto específico
export const getBudgetById = async (clientId, budgetId) => {
  try {
    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/budgets/${budgetId}`);
    return {
      success: true,
      data: response.data.data || response.data
    };
  } catch (error) {
    console.error("Error getting budget by ID:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuesto",
      status: error.response?.status
    };
  }
};

// ✅ POST /:clientId/budgets - Crear nuevo presupuesto
export const createBudget = async (clientId, budgetData) => {
  try {
    const response = await axiosWithAuth.post(`${API_URL}/workflows/p2p/${clientId}/budgets`, budgetData);
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || "Presupuesto creado exitosamente"
    };
  } catch (error) {
    console.error("Error creating budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al crear presupuesto",
      status: error.response?.status,
      details: error.response?.data?.details
    };
  }
};

// ✅ PUT /:clientId/budgets/:budgetId - Actualizar presupuesto existente
export const updateBudget = async (clientId, budgetId, budgetData) => {
  try {
    const response = await axiosWithAuth.put(`${API_URL}/workflows/p2p/${clientId}/budgets/${budgetId}`, budgetData);
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || "Presupuesto actualizado exitosamente"
    };
  } catch (error) {
    console.error("Error updating budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al actualizar presupuesto",
      status: error.response?.status,
      details: error.response?.data?.details
    };
  }
};

// ✅ DELETE /:clientId/budgets/:budgetId - Eliminar presupuesto
export const deleteBudget = async (clientId, budgetId) => {
  try {
    const response = await axiosWithAuth.delete(`${API_URL}/workflows/p2p/${clientId}/budgets/${budgetId}`);
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || "Presupuesto eliminado exitosamente"
    };
  } catch (error) {
    console.error("Error deleting budget:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al eliminar presupuesto",
      status: error.response?.status
    };
  }
};

// ✅ PATCH /:clientId/budgets/:budgetId/spent-amount - Actualizar monto gastado
export const updateSpentAmount = async (clientId, budgetId, spentAmount) => {
  try {
    const response = await axiosWithAuth.patch(`${API_URL}/workflows/p2p/${clientId}/budgets/${budgetId}/spent-amount`, {
      spent_amount: spentAmount
    });
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || "Monto gastado actualizado exitosamente"
    };
  } catch (error) {
    console.error("Error updating spent amount:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al actualizar monto gastado",
      status: error.response?.status
    };
  }
};

// ✅ GET /:clientId/budgets/entity/:entityTable/:entityId - Obtener presupuestos por entidad
export const getBudgetsByEntity = async (clientId, entityTable, entityId) => {
  try {
    const response = await axiosWithAuth.get(`${API_URL}/workflows/p2p/${clientId}/budgets/entity/${entityTable}/${entityId}`);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || response.data.data?.length || 0
    };
  } catch (error) {
    console.error("Error getting budgets by entity:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuestos por entidad",
      status: error.response?.status
    };
  }
};

// ✅ POST /:clientId/budgets/bulk - Crear múltiples presupuestos
export const bulkCreateBudgets = async (clientId, budgetsArray) => {
  try {
    const response = await axiosWithAuth.post(`${API_URL}/workflows/p2p/${clientId}/budgets/bulk`, {
      budgets: budgetsArray
    });
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || budgetsArray.length,
      message: response.data.message || `${budgetsArray.length} presupuestos creados exitosamente`
    };
  } catch (error) {
    console.error("Error creating bulk budgets:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al crear presupuestos masivamente",
      status: error.response?.status,
      details: error.response?.data?.details,
      failedItems: error.response?.data?.failedItems || []
    };
  }
};

// 📊 Funciones auxiliares para análisis y reportes

// Obtener resumen de presupuestos
export const getBudgetSummary = async (clientId, filters = {}) => {
  try {
    const response = await getBudgets(clientId, filters);
    if (!response.success) return response;

    const budgets = response.data;
    const summary = {
      totalBudgets: budgets.length,
      totalAmount: 0,
      totalSpent: 0,
      statusSummary: {},
      costCenterSummary: {},
      entitySummary: {}
    };

    budgets.forEach(budget => {
      // Totales generales
      summary.totalAmount += parseFloat(budget.amount || 0);
      summary.totalSpent += parseFloat(budget.spent_amount || 0);

      // Resumen por estado
      const status = budget.status || 'active';
      if (!summary.statusSummary[status]) {
        summary.statusSummary[status] = { count: 0, amount: 0 };
      }
      summary.statusSummary[status].count += 1;
      summary.statusSummary[status].amount += parseFloat(budget.amount || 0);

      // Resumen por centro de costo
      if (budget.cost_center_name) {
        if (!summary.costCenterSummary[budget.cost_center_name]) {
          summary.costCenterSummary[budget.cost_center_name] = { count: 0, amount: 0 };
        }
        summary.costCenterSummary[budget.cost_center_name].count += 1;
        summary.costCenterSummary[budget.cost_center_name].amount += parseFloat(budget.amount || 0);
      }

      // Resumen por entidad
      if (budget.entity_table) {
        if (!summary.entitySummary[budget.entity_table]) {
          summary.entitySummary[budget.entity_table] = { count: 0, amount: 0 };
        }
        summary.entitySummary[budget.entity_table].count += 1;
        summary.entitySummary[budget.entity_table].amount += parseFloat(budget.amount || 0);
      }
    });

    return {
      success: true,
      data: summary
    };
  } catch (error) {
    console.error("Error getting budget summary:", error);
    return {
      success: false,
      error: "Error al obtener resumen de presupuestos"
    };
  }
};

// Obtener análisis de variación presupuestal
export const getBudgetVarianceAnalysis = async (clientId, filters = {}) => {
  try {
    const budgets = await getBudgets(clientId, filters);
    if (!budgets.success) return budgets;

    let analysis = {
      totalBudget: 0,
      totalSpent: 0,
      variance: 0,
      variancePercentage: 0,
      statusAnalysis: {},
      costCenterAnalysis: {}
    };

    // Análisis general
    budgets.data.forEach(budget => {
      const amount = parseFloat(budget.amount || 0);
      const spent = parseFloat(budget.spent_amount || 0);

      analysis.totalBudget += amount;
      analysis.totalSpent += spent;
      
      // Análisis por estado
      const status = budget.status || 'active';
      if (!analysis.statusAnalysis[status]) {
        analysis.statusAnalysis[status] = {
          budget: 0,
          spent: 0,
          variance: 0,
          variancePercentage: 0
        };
      }
      analysis.statusAnalysis[status].budget += amount;
      analysis.statusAnalysis[status].spent += spent;

      // Análisis por centro de costo
      if (budget.cost_center_name) {
        if (!analysis.costCenterAnalysis[budget.cost_center_name]) {
          analysis.costCenterAnalysis[budget.cost_center_name] = {
            budget: 0,
            spent: 0,
            variance: 0,
            variancePercentage: 0
          };
        }
        analysis.costCenterAnalysis[budget.cost_center_name].budget += amount;
        analysis.costCenterAnalysis[budget.cost_center_name].spent += spent;
      }
    });

    // Calcular variaciones
    analysis.variance = analysis.totalBudget - analysis.totalSpent;
    analysis.variancePercentage = analysis.totalBudget > 0 ?
      (analysis.variance / analysis.totalBudget) * 100 : 0;

    // Calcular variaciones por estado
    Object.keys(analysis.statusAnalysis).forEach(status => {
      const statusData = analysis.statusAnalysis[status];
      statusData.variance = statusData.budget - statusData.spent;
      statusData.variancePercentage = statusData.budget > 0 ?
        (statusData.variance / statusData.budget) * 100 : 0;
    });

    // Calcular variaciones por centro de costo
    Object.keys(analysis.costCenterAnalysis).forEach(costCenter => {
      const costCenterData = analysis.costCenterAnalysis[costCenter];
      costCenterData.variance = costCenterData.budget - costCenterData.spent;
      costCenterData.variancePercentage = costCenterData.budget > 0 ?
        (costCenterData.variance / costCenterData.budget) * 100 : 0;
    });

    return {
      success: true,
      data: analysis
    };
  } catch (error) {
    console.error("Error getting budget variance analysis:", error);
    return {
      success: false,
      error: "Error al obtener análisis de variación presupuestal"
    };
  }
};

// Validar datos de presupuesto antes de enviar
export const validateBudgetData = (budgetData) => {
  const errors = [];
  
  // Validaciones requeridas
  if (!budgetData.name || budgetData.name.trim() === '') {
    errors.push('El nombre del presupuesto es requerido');
  }
  
  if (!budgetData.amount || isNaN(parseFloat(budgetData.amount)) || parseFloat(budgetData.amount) <= 0) {
    errors.push('El monto del presupuesto debe ser un número positivo');
  }
  
  if (!budgetData.currency || budgetData.currency.trim() === '') {
    errors.push('La moneda es requerida');
  }

  // Validar fechas si se proporcionan
  if (budgetData.start_date && budgetData.end_date) {
    const startDate = new Date(budgetData.start_date);
    const endDate = new Date(budgetData.end_date);

    if (startDate >= endDate) {
      errors.push('La fecha de inicio debe ser anterior a la fecha de fin');
    }
  }

  // Validar monto gastado si se proporciona
  if (budgetData.spent_amount !== undefined && budgetData.spent_amount !== null) {
    if (isNaN(parseFloat(budgetData.spent_amount)) || parseFloat(budgetData.spent_amount) < 0) {
      errors.push('El monto gastado debe ser un número no negativo');
    }

    if (parseFloat(budgetData.spent_amount) > parseFloat(budgetData.amount || 0)) {
      errors.push('El monto gastado no puede ser mayor al monto del presupuesto');
    }
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  };
};

// Formatear datos para carga masiva desde Excel
export const formatBulkBudgetData = (excelData, clientId) => {
  try {
    const formattedData = excelData.map((row, index) => {
      // Mapear al formato de la base de datos
      const budgetItem = {
        name: row.name || row.nombre || row.budget_name || '',
        amount: parseFloat(row.amount || row.monto || row.budget_amount || 0),
        currency: row.currency || row.moneda || 'COP',
        start_date: row.start_date || row.fecha_inicio || row.start_date || null,
        end_date: row.end_date || row.fecha_fin || row.end_date || null,
        status: row.status || row.estado || 'active',
        spent_amount: parseFloat(row.spent_amount || row.monto_gastado || row.spent || 0),
        cost_center_id: row.cost_center_id || row.centro_costo_id || null,
        description: row.description || row.descripcion || '',
        entity_table: row.entity_table || row.tabla_entidad || null,
        entity_id: row.entity_id || row.id_entidad || null
      };

      // Validar cada item
      const validation = validateBudgetData(budgetItem);
      if (!validation.isValid) {
        throw new Error(`Fila ${index + 1}: ${validation.errors.join(', ')}`);
      }

      return budgetItem;
    });

    return {
      success: true,
      data: formattedData
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Error al formatear datos de Excel'
    };
  }
};

